import { useEffect } from 'react';
import { BUILD_INFO } from '@/runtime/build-info';
import { ensureTelegramWebAppSdk, looksLikeTelegramRuntime } from './initData';

type TelegramViewportEvent = {
  isStateStable?: boolean;
};

type TelegramSafeAreaInsets = {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
};

type TelegramWebAppViewport = {
  expand?: () => void;
  ready?: () => void;
  viewportHeight?: number;
  viewportStableHeight?: number;
  safeAreaInset?: TelegramSafeAreaInsets;
  contentSafeAreaInset?: TelegramSafeAreaInsets;
  requestSafeArea?: () => void;
  requestContentSafeArea?: () => void;
  onEvent?: (
    event: string,
    handler: (event?: TelegramViewportEvent | TelegramSafeAreaInsets) => void,
  ) => void;
  offEvent?: (
    event: string,
    handler: (event?: TelegramViewportEvent | TelegramSafeAreaInsets) => void,
  ) => void;
};

function toPixelValue(value: number): string {
  return `${Math.round(value)}px`;
}

function applyInsetVariables(
  root: HTMLElement,
  prefix: 'system' | 'content',
  insets?: TelegramSafeAreaInsets,
) {
  if (!insets) return;

  root.style.setProperty(`--${prefix}-safe-area-top`, toPixelValue(insets.top ?? 0));
  root.style.setProperty(`--${prefix}-safe-area-right`, toPixelValue(insets.right ?? 0));
  root.style.setProperty(`--${prefix}-safe-area-bottom`, toPixelValue(insets.bottom ?? 0));
  root.style.setProperty(`--${prefix}-safe-area-left`, toPixelValue(insets.left ?? 0));
}

function getFallbackViewportHeights() {
  const visualViewportHeight = window.visualViewport?.height;
  const innerHeight = window.innerHeight;
  const dynamicHeight = visualViewportHeight && visualViewportHeight > 0
    ? visualViewportHeight
    : innerHeight;
  const stableHeight = innerHeight > 0 ? innerHeight : dynamicHeight;

  return {
    dynamicHeight,
    stableHeight,
  };
}

export function useTelegramViewport() {
  useEffect(() => {
    const root = document.documentElement;
    let frameId = 0;
    let cancelled = false;

    const getTelegramWebApp = () => window.Telegram?.WebApp as TelegramWebAppViewport | undefined;

    const syncViewport = () => {
      const tg = getTelegramWebApp();
      const { dynamicHeight, stableHeight } = getFallbackViewportHeights();
      const nextHeight =
        tg?.viewportHeight && tg.viewportHeight > 0 ? tg.viewportHeight : dynamicHeight;
      const nextStableHeight =
        tg?.viewportStableHeight && tg.viewportStableHeight > 0
          ? tg.viewportStableHeight
          : stableHeight;

      root.style.setProperty('--tg-viewport-height', toPixelValue(nextHeight));
      root.style.setProperty('--tg-stable-height', toPixelValue(nextStableHeight));
      root.style.setProperty('--app-height', toPixelValue(nextStableHeight));
      root.style.setProperty('--app-viewport-height', toPixelValue(nextHeight));
      root.style.setProperty('--app-viewport-stable-height', toPixelValue(nextStableHeight));
      root.style.setProperty('--app-vh', toPixelValue(nextHeight));
      root.style.setProperty('--app-vh-stable', toPixelValue(nextStableHeight));
      applyInsetVariables(root, 'system', tg?.safeAreaInset);
      applyInsetVariables(root, 'content', tg?.contentSafeAreaInset);
      root.dataset.runtimeBuild = BUILD_INFO.sha;
      root.dataset.telegramRuntime = tg ? 'true' : 'false';
    };

    const scheduleSync = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(syncViewport);
    };

    const attachTelegramHandlers = () => {
      const tg = getTelegramWebApp();
      tg?.expand?.();
      tg?.ready?.();
      tg?.requestSafeArea?.();
      tg?.requestContentSafeArea?.();

      tg?.onEvent?.('viewportChanged', handleViewportChanged);
      tg?.onEvent?.('safeAreaChanged', handleSafeAreaChanged);
      tg?.onEvent?.('safe_area_changed', handleSafeAreaChanged);
      tg?.onEvent?.('contentSafeAreaChanged', handleContentSafeAreaChanged);
      tg?.onEvent?.('content_safe_area_changed', handleContentSafeAreaChanged);
    };

    const handleViewportChanged = () => {
      scheduleSync();
    };

    const handleSafeAreaChanged = (event?: TelegramViewportEvent | TelegramSafeAreaInsets) => {
      if (event && ('top' in event || 'bottom' in event || 'left' in event || 'right' in event)) {
        applyInsetVariables(root, 'system', event);
      } else {
        scheduleSync();
      }
    };

    const handleContentSafeAreaChanged = (event?: TelegramViewportEvent | TelegramSafeAreaInsets) => {
      if (event && ('top' in event || 'bottom' in event || 'left' in event || 'right' in event)) {
        applyInsetVariables(root, 'content', event);
      } else {
        scheduleSync();
      }
    };

    const bootstrap = async () => {
      if (looksLikeTelegramRuntime()) {
        await ensureTelegramWebAppSdk();
      }
      if (cancelled) return;
      attachTelegramHandlers();
      scheduleSync();
    };

    void bootstrap();
    window.addEventListener('resize', scheduleSync);
    window.visualViewport?.addEventListener('resize', scheduleSync);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frameId);
      const tg = getTelegramWebApp();
      tg?.offEvent?.('viewportChanged', handleViewportChanged);
      tg?.offEvent?.('safeAreaChanged', handleSafeAreaChanged);
      tg?.offEvent?.('safe_area_changed', handleSafeAreaChanged);
      tg?.offEvent?.('contentSafeAreaChanged', handleContentSafeAreaChanged);
      tg?.offEvent?.('content_safe_area_changed', handleContentSafeAreaChanged);
      window.removeEventListener('resize', scheduleSync);
      window.visualViewport?.removeEventListener('resize', scheduleSync);
    };
  }, []);
}
